/**
 * Agent Detail Page
 * Individual agent profile, performance trends, and conversation history
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Bot, UserCheck } from 'lucide-react';
import MetricCard from '../components/dashboard/MetricCard';
import TrendChart from '../components/dashboard/TrendChart';
import AnalysisCard from '../components/dashboard/AnalysisCard';
import AnalysisDetailModal, { MiniBar } from '../components/dashboard/AnalysisDetailModal';
import AgentSyncHint from '../components/dashboard/AgentSyncHint';
import DateRangeFilter from '../components/dashboard/DateRangeFilter';
import Spinner from '../components/shared/Spinner';
import EmptyState from '../components/shared/EmptyState';
import RefreshButton from '../components/shared/RefreshButton';
import Pagination from '@shared/components/Pagination';
import useDateRange from '../hooks/useDateRange';
import usePagination from '@shared/hooks/usePagination';
import { getAgentPerformance } from '../services/analyticsService';
import { getAnalysesList } from '../services/sentimentService';
import { toTitleCase, sentimentToPercent, formatShortDate } from '../utils/formatters';

const AgentDetailPage = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialRange = (() => {
    const f = searchParams.get('from');
    const t = searchParams.get('to');
    return f && t ? { from: f, to: t } : undefined;
  })();
  const { from, to, onChange } = useDateRange(initialRange);
  const { page, pageSize, setPage, setPageSize } = usePagination(1, 10);

  const [loading, setLoading] = useState(true);
  const [agentData, setAgentData] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [totalAnalyses, setTotalAnalyses] = useState(0);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [perfResult, analysesResult] = await Promise.all([
        getAgentPerformance(agentId, { from, to }),
        getAnalysesList({ agent_id: agentId, from_date: from, to_date: to, page, page_size: pageSize }),
      ]);
      if (perfResult.success) setAgentData(perfResult.data);
      else toast.error(perfResult.error);
      if (analysesResult.success) {
        setAnalyses(analysesResult.data.analyses || []);
        setTotalAnalyses(analysesResult.data.total || 0);
      } else {
        toast.error(analysesResult.error);
      }
    } catch (err) {
      toast.error('Failed to load agent data');
    } finally {
      setLoading(false);
    }
  }, [agentId, from, to, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Spinner />;

  const agent = agentData || {};
  // Phone number, extension, and the raw ID when the header shows a name instead
  const identityLine = [
    agent.phone_number,
    agent.extension && `Ext: ${agent.extension}`,
    agent.name && agent.name !== agentId ? agentId : null,
  ].filter((value) => value && value !== agent.name).join(' · ');
  const qualityDimensions = agent.quality_dimensions || agent.agent_quality || {};
  const trendData = agent.trend_data || [];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/agent')}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="flex items-center space-x-2">
              <h2 className="text-lg font-bold text-gray-900">{agent.name || agentId}</h2>
              {agent.agent_type && (
                agent.agent_type === 'ai' ? (
                  <span className="inline-flex items-center space-x-1 text-xs px-2 py-0.5 rounded bg-purple-50 text-purple-600">
                    <Bot size={12} />
                    <span>AI Agent</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center space-x-1 text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-600">
                    <UserCheck size={12} />
                    <span>Human Agent</span>
                  </span>
                )
              )}
              <AgentSyncHint status={agent.sync_status} />
            </div>
            {identityLine && (
              <p className="text-xs text-gray-400 mt-0.5">{identityLine}</p>
            )}
          </div>
          <RefreshButton onClick={fetchData} loading={loading} />
        </div>
        <DateRangeFilter from={from} to={to} onChange={onChange} />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <MetricCard label="Interactions" value={agent.total_interactions ?? 0} color="#6366f1" />
        <MetricCard label="Avg Sentiment" value={agent.avg_sentiment != null ? sentimentToPercent(agent.avg_sentiment) + '%' : '-'} color="#22c55e" />
        <MetricCard label="Avg Handle Time" value={agent.avg_handle_time != null ? `${Math.round(agent.avg_handle_time)}s` : '-'} color="#3b82f6" />
        <MetricCard label="Resolution Rate" value={agent.resolution_rate != null ? `${agent.resolution_rate}%` : '-'} color="#f59e0b" />
      </div>

      {/* Trend Chart + Quality Dimensions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2">
          {trendData.length > 0 ? (
            <TrendChart
              title="Performance Trend"
              data={trendData}
              xKey="date"
              xFormatter={formatShortDate}
              lines={[
                {
                  dataKey: 'avg_sentiment',
                  color: '#22c55e',
                  label: 'Sentiment',
                  yAxisId: 'left',
                  domain: [-1, 1],
                  tickFormatter: (v) => sentimentToPercent(v) + '%',
                  formatter: (v) => sentimentToPercent(v) + '%',
                },
                {
                  dataKey: 'interaction_count',
                  color: '#6366f1',
                  label: 'Interactions',
                  yAxisId: 'right',
                },
              ]}
              height={250}
            />
          ) : (
            <div className="card p-5 h-full flex items-center justify-center">
              <p className="text-sm text-gray-400">No trend data available yet</p>
            </div>
          )}
        </div>
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Quality Dimensions</h3>
          {Object.keys(qualityDimensions).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(qualityDimensions).map(([key, value]) => (
                <MiniBar key={key} label={toTitleCase(key)} value={value} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No quality data available yet</p>
          )}
        </div>
      </div>

      {/* Agent's Conversations */}
      <div className="card">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700">
            Conversations
            {totalAnalyses > 0 && <span className="ml-1.5 text-xs font-normal text-gray-400">({totalAnalyses})</span>}
          </h3>
        </div>
        <div className="p-5">
          {analyses.length === 0 ? (
            <EmptyState title="No conversations yet" description="Conversations will appear when this agent processes interactions." />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {analyses.map(analysis => (
                  <AnalysisCard
                    key={analysis.id}
                    analysis={analysis}
                    compact
                    onViewDetails={setSelectedAnalysis}
                  />
                ))}
              </div>
              <Pagination
                page={page}
                pageSize={pageSize}
                total={totalAnalyses}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                loading={loading}
              />
            </>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedAnalysis && (
        <AnalysisDetailModal
          analysis={selectedAnalysis}
          onClose={() => setSelectedAnalysis(null)}
        />
      )}
    </div>
  );
};

export default AgentDetailPage;
